import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ANON_LOOKUP_QUOTA_PER_DAY, API_EXAMPLES } from '../../lib/api-examples.js';

/**
 * Copy-paste API examples for people who have not signed up.
 *
 * Every command here runs with NO credentials, and each was executed against production before being
 * published: submit returns 202, the hash lookup 200, the URL scan 202, the result poll 200. That
 * matters more than it sounds, because the API reference had been telling readers a key was required
 * for all four, which is the opposite of the adoption path we want.
 *
 * TWO THINGS THE EXAMPLES MUST NOT GET WRONG. The submit endpoint takes JSON with base64 bytes, and a
 * multipart `-F file=@...` upload returns HTTP 500 - an example using it would fail on the reader's
 * very first attempt. And anonymous access is metered rather than unlimited (submissions are
 * unlimited, dataset lookups are 1,000/day per IP, measured from the `x-daily-lookup-limit` header),
 * so the card says so rather than implying an open door.
 *
 * The commands themselves live in `lib/api-examples.ts` so a test can pin them without importing
 * React, and so this file stays presentation only.
 */

/**
 * `dark` renders on the navy hero, `surface` inside the submit tool. One component in two places so
 * the two copies of these commands cannot drift apart.
 */
export function ApiQuickstart({ tone = 'surface' }: { tone?: 'dark' | 'surface' }) {
  const [active, setActive] = useState(API_EXAMPLES[0]!.id);
  const [copied, setCopied] = useState(false);
  const example = API_EXAMPLES.find((e) => e.id === active) ?? API_EXAMPLES[0]!;
  const dark = tone === 'dark';

  const copy = () => {
    void navigator.clipboard.writeText(example.command).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div
      className={
        dark
          ? 'rounded-md border border-white/15 bg-black/25 p-3 sm:p-4'
          : 'rounded-md border border-border-subtle bg-bg-raised p-3 sm:p-4'
      }
    >
      <div className="mb-2">
        <div className="flex flex-wrap gap-x-4 gap-y-1" role="tablist" aria-label="API examples">
          {API_EXAMPLES.map((e) => {
            const on = e.id === active;
            return (
              <button
                key={e.id}
                role="tab"
                aria-selected={on}
                onClick={() => setActive(e.id)}
                className={`-mb-px border-b-2 pb-1 text-xs font-semibold transition-colors ${
                  on
                    ? dark
                      ? 'border-accent-500 text-white'
                      : 'border-accent-500 text-text-primary'
                    : dark
                      ? 'border-transparent text-white/55 hover:text-white/85'
                      : 'border-transparent text-text-muted hover:text-text-primary'
                }`}
              >
                {e.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* The endpoint and the copy control sit ON their own row rather than floating over the code.
          An absolutely positioned copy button (the pattern used on the API reference page) covers the
          first line of a long single-line command once the block starts scrolling horizontally, which
          on a phone hides the very part the reader needs to see. */}
      <div className="mb-1 flex items-center justify-between gap-2">
        <code className={`truncate font-mono text-micro ${dark ? 'text-white/45' : 'text-text-muted'}`}>
          {example.endpoint}
        </code>
        <button
          onClick={copy}
          className={`shrink-0 rounded border px-2 py-1 text-xs transition-colors ${
            dark
              ? 'border-white/20 bg-white/10 text-white/80 hover:text-white'
              : 'border-border-default bg-bg-surface text-text-muted hover:text-text-primary'
          }`}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <pre
        className={`overflow-x-auto rounded p-3 font-mono text-xs leading-relaxed ${
          dark ? 'bg-black/40 text-white/85' : 'bg-bg-base text-text-secondary'
        }`}
      >
        {example.command}
      </pre>

      <p className={`mt-2 text-micro ${dark ? 'text-white/55' : 'text-text-muted'}`}>{example.note}</p>

      <p className={`mt-2 text-micro ${dark ? 'text-white/70' : 'text-text-secondary'}`}>
        No API key required. Submissions unlimited; lookups{' '}
        {ANON_LOOKUP_QUOTA_PER_DAY.toLocaleString('en-US')}/day per IP.{' '}
        <Link
          to="/docs"
          className={dark ? 'underline decoration-white/30 hover:text-white' : 'underline hover:text-text-primary'}
        >
          Full API reference
        </Link>
      </p>
    </div>
  );
}
